import React, { useEffect, useMemo, useState } from 'react';
import { BackHandler, Platform, View } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import ServiceNoteButton from '../driver/components/ServiceNoteButton';
import { Alert } from '../../components/other/Alert';
import gql from 'graphql-tag';

import { GET_SERVICE_DOCKET_FOR_CONFIRM_STOCK_LEVELS } from '../../../common/graphql/serviceDocket';
import { useEventCallback } from '../../../common/utils/hooks';
import { Screen } from '../../design/components';
import { useFreshDetailQueryOptions } from '../../hooks';
import useBackLabel from '../../hooks/useBackLabel';
import useFramedDetailStates from '../../hooks/useFramedDetailStates';
import { useOfflineQueue } from '../../offline';
import ConfirmCoffeeMachineValues from './ConfirmCoffeeMachineValues';
import ConfirmMachineValues from './ConfirmMachineValues';
import TrainingModeBanner from '../myDay/TrainingModeBanner';
import { isMyDayTraining } from '../myDay/myDayTraining';

function openWarning (navigation, confirmValues, disableConfirm = false) {
	Alert.alert(
		'Warning',
		disableConfirm
			? 'Going back will discard any changes you have made. Reconnect before confirming training changes.'
			: 'Going back will discard any changes you have made. To save your current changes and go back, select "Confirm and go back".',
		[
			...(!disableConfirm ? [{
				text: 'Confirm and go back',
				onPress: async () => {
					if (confirmValues) {
						await confirmValues(true);
					}
					navigation.goBack();
				},
			}] : []),
			{
				text: 'Go back',
				onPress: () => navigation.goBack(),
			},
			{ text: 'Cancel', onPress: () => {}, style: 'cancel' },
		],
		{ cancelable: false },
	);
}

const ConfirmCoffeeMachineValuesView = () => {
	const navigation = useNavigation();
	const route = useRoute();
	const { isOnline } = useOfflineQueue();
	const docketId = route.params?.docketId;
	const isCoffee = route.params?.isCoffee;
	const trainingContext = route.params?.myDayTraining;
	const isTraining = isMyDayTraining(trainingContext);
	const {
		canRenderDetailQuery,
		detailQueryOptions,
		blockDataWhileLoading,
	} = useFreshDetailQueryOptions({ noCache: isTraining });

	const [anyUnsavedChanges, setAnyUnsavedChanges] = useState(false);
	const [confirmValues, setConfimValues] = useState(null);

	const onBackPress = useEventCallback(() => {
		if (!anyUnsavedChanges) {
			return navigation.goBack();
		}

		openWarning(navigation, confirmValues, isTraining && !isOnline);
		return true;
	});

	useEffect(() => {
		// BackHandler is only supported on Android
		if (Platform.OS === 'android') {
			const backHandler = BackHandler.addEventListener('hardwareBackPress', onBackPress);
			return () => backHandler.remove();
		}
	}, [onBackPress]);

	const backLabel = useBackLabel();
	const banner = useMemo(() => (
		<TrainingModeBanner trainingContext={trainingContext} isOnline={isOnline} />
	), [isOnline, trainingContext]);
	// Header back: same warning as the Android back button.
	const framedStates = useFramedDetailStates({ headerRight: <ServiceNoteButton />, backLabel, onBack: onBackPress, banner });
	const frameProps = { backLabel, onBack: onBackPress, banner, ...framedStates };

	if (!canRenderDetailQuery) {
		return <Screen headerRight={<ServiceNoteButton />} backLabel={backLabel} onBack={onBackPress} banner={banner} isLoading={true} />;
	}

	return (
		<View style={{ flex: 1 }}>
			{isCoffee ? (
				<ConfirmCoffeeMachineValues
					id={docketId}
					query={coffeeQuery}
					setAnyUnsavedChanges={setAnyUnsavedChanges}
					setConfimValues={setConfimValues}
					anyUnsavedChanges={anyUnsavedChanges}
					detailQueryOptions={detailQueryOptions}
					blockDataWhileLoading={blockDataWhileLoading}
					disableActions={isTraining && !isOnline}
					adminTraining={isTraining}
					{...frameProps}
				/>
			) : (
				<ConfirmMachineValues
					id={docketId}
					query={GET_SERVICE_DOCKET_FOR_CONFIRM_STOCK_LEVELS}
					setAnyUnsavedChanges={setAnyUnsavedChanges}
					setConfimValues={setConfimValues}
					anyUnsavedChanges={anyUnsavedChanges}
					detailQueryOptions={detailQueryOptions}
					blockDataWhileLoading={blockDataWhileLoading}
					trainingContext={trainingContext}
					{...frameProps}
				/>
			)}
		</View>
	);
};

export default ConfirmCoffeeMachineValuesView;

const coffeeQuery = gql`
	query($id: String) {
		getServiceDocket(id: $id) {
			id
			date

			status
			isRestocked
			salesMeter
			pickList {
				id
				pickListItems {
					id
					planogramItem {
						id
						quantity
						parlevel
						capacity
						row
						column
						enabled
						parlevelUnlocked
					}
					product {
						id
						imageUrl
						HCGCategory
						HFDCategory
						name
						size
						sizeUnit
						measurementUnit
					}
					selection
					quantity
					quantityProductSizeUnit
					productSize
					productSizeUnit
					productMeasurementUnit
				}
			}
			isIngredientRestock

			previousDocket {
				id
				salesMeter
				planned
			}

			machine {
				id
				assetId
				place {
					id
					name
				}
				location {
					id
					name
				}
				isOffline
			}
		}
	}
`;
